/**
 * Lenis Smooth Scroll
 *
 * Site-wide smooth scrolling powered by Lenis. Bundled by webpack into
 * assets/js/build/smooth-scroll.js and enqueued from
 * inc/enqueue/assets-frontend.php.
 *
 * Tuning: every option below can be overridden per-site from PHP via the
 * `rothcocpa_lenis_settings` filter (see assets-frontend.php), which is
 * exposed to this script as `window.rothcocpaLenis`.
 *
 * @since 1.0.0
 */

import Lenis from 'lenis';

( function () {
	'use strict';

	/**
	 * Default Lenis options.
	 *
	 * duration      - Seconds it takes for the scroll to catch up to the target.
	 *                 Higher = slower/heavier glide, lower = snappier.
	 * wheelMultiplier - Distance travelled per mouse-wheel tick (1 = native).
	 * touchMultiplier - Distance travelled per touch drag.
	 * lerp          - Alternative to `duration`: per-frame interpolation factor
	 *                 (0-1). Only used when `duration` is null.
	 */
	const DEFAULTS = {
		duration: 1.2,
		wheelMultiplier: 1,
		touchMultiplier: 1.5,
		smoothWheel: true,
		syncTouch: false,
		gestureOrientation: 'vertical',
		autoResize: true,
	};

	const settings = Object.assign( {}, DEFAULTS, window.rothcocpaLenis || {} );

	// Respect the visitor's reduced-motion preference.
	const prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches;

	if ( prefersReducedMotion || settings.enabled === false ) {
		return;
	}

	const lenis = new Lenis( {
		duration: settings.duration,
		lerp: settings.duration ? undefined : settings.lerp,
		easing: ( t ) => Math.min( 1, 1.001 - Math.pow( 2, -10 * t ) ),
		orientation: 'vertical',
		gestureOrientation: settings.gestureOrientation,
		smoothWheel: settings.smoothWheel,
		syncTouch: settings.syncTouch,
		wheelMultiplier: settings.wheelMultiplier,
		touchMultiplier: settings.touchMultiplier,
		autoResize: settings.autoResize,
	} );

	// Expose the instance so other theme scripts can drive it
	// (lenis.scrollTo(), lenis.stop(), lenis.start(), lenis.on('scroll', ...)).
	window.lenisInstance = lenis;

	function raf( time ) {
		lenis.raf( time );
		requestAnimationFrame( raf );
	}
	requestAnimationFrame( raf );

	/**
	 * Sticky-header offset used when jumping to in-page anchors.
	 *
	 * @return {number} Header height in pixels.
	 */
	function getHeaderOffset() {
		const header = document.querySelector( 'header' );
		return header ? header.offsetHeight : 0;
	}

	/**
	 * Route same-page anchor clicks through Lenis so they animate with the
	 * same easing as wheel scrolling instead of jumping.
	 *
	 * Skips links that opt out with `data-lenis-ignore` and links handled by
	 * assets/js/anchor-scroll-offset.js inside navigation blocks (that script
	 * defers to `window.lenisInstance` when present).
	 *
	 * @param {Event} event Click event.
	 */
	function handleAnchorClick( event ) {
		const link = event.target.closest( 'a[href*="#"]' );

		if ( ! link || link.hasAttribute( 'data-lenis-ignore' ) ) {
			return;
		}

		// Navigation-block anchors are owned by anchor-scroll-offset.js.
		if ( link.closest( '.wp-block-navigation' ) ) {
			return;
		}

		const url = new URL( link.getAttribute( 'href' ), window.location.href );

		if (
			url.pathname !== window.location.pathname ||
			url.search !== window.location.search ||
			! url.hash ||
			url.hash === '#'
		) {
			return;
		}

		const target = document.getElementById( url.hash.substring( 1 ) );

		if ( ! target ) {
			return;
		}

		event.preventDefault();
		lenis.scrollTo( target, { offset: -getHeaderOffset() } );
	}

	document.addEventListener( 'click', handleAnchorClick );

	// Pause smooth scrolling while a scroll-locking overlay is open. Overlays
	// such as the search overlay lock the page with `body { overflow: hidden }`,
	// which Lenis ignores because it drives the scroll itself, so mirror that
	// lock onto the instance. Scrollable panels inside an overlay should carry
	// `data-lenis-prevent` so their own scrolling keeps working.
	function syncScrollLock() {
		const locked = document.body.style.overflow === 'hidden';

		if ( locked === lenis.isStopped ) {
			return;
		}

		if ( locked ) {
			lenis.stop();
		} else {
			lenis.start();
		}
	}

	function watchScrollLock() {
		new MutationObserver( syncScrollLock ).observe( document.body, {
			attributes: true,
			attributeFilter: [ 'style' ],
		} );
		syncScrollLock();
	}

	if ( document.body ) {
		watchScrollLock();
	} else {
		document.addEventListener( 'DOMContentLoaded', watchScrollLock );
	}

	// Overlays that lock scrolling some other way can drive Lenis directly.
	document.addEventListener( 'rothcocpa/scroll-lock', () => lenis.stop() );
	document.addEventListener( 'rothcocpa/scroll-unlock', () => lenis.start() );
} )();
